import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Typography, Progress, Tag, Tooltip } from 'antd';
import {
  DatabaseOutlined,
  CloudUploadOutlined,
  HddOutlined,
  LinkOutlined,
  DesktopOutlined,
  SafetyCertificateOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import { DataCard, StatCard } from '../common';
import { useTenant } from '../../context/TenantContext';
import {
  useAcronisIdFromCipp,
  useAcronisResources,
  useAcronisHealth,
  useAcronisProtectionPolicies,
  useAcronisResourceUsage,
  useAcronisM365Backup,
} from '../../hooks';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const HealthIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HealthDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'healthy': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'critical': return theme.colors.error;
      default: return theme.colors.textMuted;
    }
  }};
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-status-awaiting-border);
  border-radius: 4px;
  color: var(--odin-status-awaiting-text);
  font-size: 12px;
  margin-bottom: 12px;
`;

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '--';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// MCP tool results arrive as a JSON string, a direct { data } object, or an
// MCP content-array envelope.
const parseMCPResponse = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (raw.data) return raw;
  if (raw.content?.[0]?.text) {
    try {
      return JSON.parse(raw.content[0].text);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * BackupWidget - Displays Acronis backup data
 *
 * This component integrates with Acronis-MCP to display:
 * - Protected resources count (using get_protected_resources for accuracy)
 * - Backup success rate
 * - Storage used
 * - Jobs in last 24 hours
 * - Microsoft 365 protected seats / sites / Teams (get_m365_backup_status)
 *
 * Uses customerMappingService (via useAcronisIdFromCipp) to resolve
 * CIPP tenant IDs to Acronis tenant IDs.
 *
 * @param {Object} props
 * @param {string} props.tenantId - Optional direct Acronis tenant ID (overrides mapping)
 * @param {Object} props.data - Legacy data prop for backward compatibility
 * @param {boolean} props.loading - Loading state (legacy)
 * @param {Object} props.error - Error object (legacy)
 * @param {Function} props.onRefresh - Refresh callback
 * @param {boolean} props.compact - Show compact StatCard view
 */
const BackupWidget = ({
  tenantId: propTenantId = null,
  data: legacyData = null,
  loading: legacyLoading = false,
  error: legacyError = null,
  onRefresh,
  compact = false,
}) => {
  // Get selected tenant from context (CIPP tenant ID)
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Acronis tenant ID using customer mapping service
  const {
    acronisId: mappedAcronisId,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useAcronisIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId && !propTenantId,
  });

  // Use prop tenantId if provided, otherwise use mapped Acronis ID
  const acronisId = propTenantId || mappedAcronisId;
  const hasAcronisMapping = !!propTenantId || isResolved;

  // Fetch Acronis protected resources data (using get_protected_resources for accurate counts)
  const {
    data: resourcesData,
    isLoading: resourcesLoading,
    error: resourcesError,
    refetch: refetchResources,
  } = useAcronisResources(acronisId, {
    enabled: !!acronisId,
  });

  // Fetch Acronis health data (for storage and job stats)
  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useAcronisHealth(acronisId, {
    enabled: !!acronisId,
  });

  // Fetch Acronis resource usage (storage data)
  const {
    data: usageData,
    isLoading: usageLoading,
    error: usageError,
    refetch: refetchUsage,
  } = useAcronisResourceUsage(acronisId, {
    enabled: !!acronisId,
  });

  // Fetch Acronis protection policies (backup plans)
  const {
    data: policiesData,
    isLoading: policiesLoading,
    error: policiesError,
    refetch: refetchPolicies,
  } = useAcronisProtectionPolicies(acronisId, {
    enabled: !!acronisId,
  });

  // Cloud-to-cloud M365 backups create NO resource rows and no protection
  // policies, so for an M365-only customer everything above reads 0 and the
  // card used to show "No data". get_m365_backup_status (usage counters) is
  // the only Acronis evidence those customers have — same source as AcronisPage.
  const {
    data: m365Data,
    isLoading: m365Loading,
    refetch: refetchM365,
  } = useAcronisM365Backup(acronisId, {
    enabled: !!acronisId,
  });

  const m365 = useMemo(() => {
    const d = parseMCPResponse(m365Data)?.data;
    if (!d?.enabled) return null;
    return {
      seats: d.protectedItems?.seats ?? 0,
      sharepointSites: d.protectedItems?.sharepointSites ?? 0,
      teams: d.protectedItems?.teams ?? 0,
      storageBytes: d.cloudToCloudStorageBytes ?? 0,
    };
  }, [m365Data]);

  // Calculate metrics from Acronis data
  const metrics = useMemo(() => {
    // If legacy data is provided, use it for backward compatibility
    if (legacyData && Object.keys(legacyData).length > 0) {
      return {
        resourceCount: legacyData.resourceCount ?? null,
        storageUsed: legacyData.storageUsed ?? null,
        storageQuota: legacyData.storageQuota ?? null,
        healthStatus: null,
        agentCount: null,
        policyCount: null,
        enabledPolicies: null,
      };
    }

    // Parse resources from Acronis-MCP response
    // The response format from get_protected_resources is: { success, data: { resources, summary, byType, byCategory }, markdown }
    let parsedResources = null;
    if (resourcesData) {
      // Handle MCP response structure (may be wrapped in content array or direct JSON)
      if (typeof resourcesData === 'string') {
        try {
          parsedResources = JSON.parse(resourcesData);
        } catch {
          parsedResources = null;
        }
      } else if (resourcesData.data) {
        parsedResources = resourcesData;
      } else if (resourcesData.content?.[0]?.text) {
        try {
          parsedResources = JSON.parse(resourcesData.content[0].text);
        } catch {
          parsedResources = null;
        }
      }
    }

    // Parse resource usage data from Acronis-MCP response (for storage)
    let parsedUsage = null;
    if (usageData) {
      if (typeof usageData === 'string') {
        try {
          parsedUsage = JSON.parse(usageData);
        } catch {
          parsedUsage = null;
        }
      } else if (usageData.data) {
        parsedUsage = usageData;
      } else if (usageData.content?.[0]?.text) {
        try {
          parsedUsage = JSON.parse(usageData.content[0].text);
        } catch {
          parsedUsage = null;
        }
      }
    }

    // Parse protection policies data from Acronis-MCP response
    let parsedPolicies = null;
    if (policiesData) {
      if (typeof policiesData === 'string') {
        try {
          parsedPolicies = JSON.parse(policiesData);
        } catch {
          parsedPolicies = null;
        }
      } else if (policiesData.data) {
        parsedPolicies = policiesData;
      } else if (policiesData.content?.[0]?.text) {
        try {
          parsedPolicies = JSON.parse(policiesData.content[0].text);
        } catch {
          parsedPolicies = null;
        }
      }
    }

    // Extract resource count from protected resources
    const resources = parsedResources?.data?.resources || [];
    const resourceCount = parsedResources?.data?.summary?.total ?? resources.length;

    // Extract storage from resource usage (NOT health/backup_status)
    // Note: field names use underscores (storage_used), not camelCase
    const usageEntry = parsedUsage?.data?.usage?.[0];
    const storageUsed = usageEntry?.storage_used ?? null;
    const storageQuota = usageEntry?.storage_quota ?? null;

    // Calculate agent count from protected resources (endpoints + servers)
    // Protected machines MUST have agents installed - can't be protected otherwise
    // This is more reliable than get_agent_inventory which has tenant hierarchy issues
    const byCategory = parsedResources?.data?.summary?.byCategory || {};
    const agentCount = (byCategory.endpoints || 0) + (byCategory.servers || 0);

    // Determine health status based on having protected resources
    const healthStatus = resourceCount > 0 ? 'healthy' : 'warning';

    // Extract policies from protection policies
    // Note: Acronis API returns nested structure: data.policies[0].policy contains the array
    const policiesArray = parsedPolicies?.data?.policies?.[0]?.policy ||
                          parsedPolicies?.data?.policies ||
                          parsedPolicies?.policies ||
                          [];
    // Filter for backup-related policies (exclude pure security policies)
    const backupPolicies = policiesArray.filter(p =>
      p.type?.includes('backup') ||
      p.type?.includes('protection.disaster_recovery') ||
      p.type?.includes('protection.total')
    );
    const policyCount = backupPolicies.length || policiesArray.length; // Fall back to all if no backup specific
    const enabledPolicies = (backupPolicies.length > 0 ? backupPolicies : policiesArray).filter(p => p.enabled !== false).length;

    return {
      resourceCount: resourceCount || null,
      storageUsed,
      storageQuota,
      healthStatus,
      agentCount,
      policyCount,
      enabledPolicies,
    };
  }, [legacyData, resourcesData, usageData, policiesData]);

  // Calculate storage percentage
  const storagePercent = metrics.storageUsed && metrics.storageQuota
    ? Math.round((metrics.storageUsed / metrics.storageQuota) * 100)
    : null;

  // Determine loading state
  const isLoading = legacyLoading || isMappingLoading || resourcesLoading || healthLoading || usageLoading || policiesLoading || m365Loading;

  // Determine error state
  const error = legacyError || resourcesError || healthError || usageError || policiesError;

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
    if (acronisId) {
      refetchResources();
      refetchHealth();
      refetchUsage();
      refetchPolicies();
      refetchM365();
    }
  }, [onRefresh, acronisId, refetchResources, refetchHealth, refetchUsage, refetchPolicies, refetchM365]);

  // Show mapping status warning if no Acronis mapping found
  const showMappingWarning = cippTenantId && !hasAcronisMapping && !isMappingLoading && !propTenantId;

  // Compact view (StatCard)
  if (compact) {
    return (
      <StatCard
        icon={DatabaseOutlined}
        value={metrics.resourceCount}
        label="Protected Resources"
        loading={isLoading}
        tooltip={
          hasAcronisMapping
            ? `Resources protected by Acronis${customer ? ` (${customer.displayName})` : ''}`
            : 'Configure Acronis mapping in Odin-MCP to view data'
        }
      />
    );
  }

  // Full widget view
  return (
    <DataCard
      title="Acronis"
      icon={DatabaseOutlined}
      loading={isLoading}
      error={error}
      onRefresh={handleRefresh}
      empty={!metrics.resourceCount && !metrics.policyCount && !m365 && !showMappingWarning}
      extra={
        metrics.healthStatus && metrics.resourceCount && (
          <Tooltip title={`Backup health: ${metrics.healthStatus}`}>
            <HealthIndicator>
              <HealthDot $status={metrics.healthStatus} />
              <Text style={{ fontSize: 12, color: 'var(--odin-text-secondary)' }}>
                {metrics.healthStatus === 'healthy' ? 'Healthy' :
                 metrics.healthStatus === 'warning' ? 'Warning' : 'Critical'}
              </Text>
            </HealthIndicator>
          </Tooltip>
        )
      }
    >
      {showMappingWarning && (
        <MappingWarning>
          <LinkOutlined />
          <span>
            No Acronis mapping found for this tenant. Add mapping in Odin-MCP config.
          </span>
        </MappingWarning>
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <CloudUploadOutlined />
              Protected Resources
            </MetricLabel>
            <MetricValue>{metrics.resourceCount ?? '--'}</MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <SafetyCertificateOutlined />
              Backup Plans
            </MetricLabel>
            <MetricValue>
              {metrics.policyCount !== null && metrics.policyCount !== undefined ? (
                <>
                  {metrics.enabledPolicies}/{metrics.policyCount}
                  <Tooltip title="Enabled / Total backup plans">
                    <Tag color="blue" style={{ marginLeft: 8 }}>Active</Tag>
                  </Tooltip>
                </>
              ) : '--'}
            </MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <HddOutlined />
              Storage Used
            </MetricLabel>
            <MetricValue>
              {metrics.storageUsed !== null ? formatBytes(metrics.storageUsed) : '--'}
              {metrics.storageQuota > 0 && ` / ${formatBytes(metrics.storageQuota)}`}
            </MetricValue>
          </MetricRow>
          {storagePercent !== null && (
            <Progress
              percent={storagePercent}
              size="small"
              strokeColor={storagePercent >= 90 ? 'var(--odin-error)' : storagePercent >= 70 ? 'var(--odin-warning)' : 'var(--odin-success)'}
              style={{ marginTop: 8 }}
            />
          )}
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <DesktopOutlined />
              Agents Installed
            </MetricLabel>
            <Tooltip title="Count of protected endpoints and servers (machines with backup agents)">
              <MetricValue>{metrics.agentCount ?? '--'}</MetricValue>
            </Tooltip>
          </MetricRow>
        </Col>
        {m365 && (
          <>
            <Col span={12}>
              <MetricRow>
                <MetricLabel>
                  <CloudOutlined />
                  M365 Seats
                </MetricLabel>
                <Tooltip title="Microsoft 365 users protected by Acronis cloud-to-cloud backup (billing counters, ~6 h lag)">
                  <MetricValue>{m365.seats}</MetricValue>
                </Tooltip>
              </MetricRow>
            </Col>
            <Col span={12}>
              <MetricRow>
                <MetricLabel>
                  <CloudOutlined />
                  SharePoint Sites
                </MetricLabel>
                <MetricValue>{m365.sharepointSites}</MetricValue>
              </MetricRow>
            </Col>
            <Col span={12}>
              <MetricRow>
                <MetricLabel>
                  <CloudOutlined />
                  Teams
                </MetricLabel>
                <MetricValue>{m365.teams}</MetricValue>
              </MetricRow>
            </Col>
            <Col span={12}>
              <MetricRow>
                <MetricLabel>
                  <HddOutlined />
                  M365 Storage
                </MetricLabel>
                <MetricValue>{formatBytes(m365.storageBytes)}</MetricValue>
              </MetricRow>
            </Col>
          </>
        )}
      </Row>
    </DataCard>
  );
};

BackupWidget.propTypes = {
  /** Direct Acronis tenant ID (bypasses customer mapping) */
  tenantId: PropTypes.string,
  /** Legacy data prop for backward compatibility */
  data: PropTypes.shape({
    resourceCount: PropTypes.number,
    storageUsed: PropTypes.number,
    storageQuota: PropTypes.number,
  }),
  /** Legacy loading state */
  loading: PropTypes.bool,
  /** Legacy error object */
  error: PropTypes.object,
  /** Callback when refresh is requested */
  onRefresh: PropTypes.func,
  /** Show compact StatCard view */
  compact: PropTypes.bool,
};

export default BackupWidget;
